// @ts-nocheck
import { useState, useEffect } from 'react';
import useApiCalls from '../hooks/useApiCalls';
import { DEFAULT_GENERATION_OPTIONS } from '../utils/constants';
import { jobSession, logger } from '../utils/helpers';

const QuestionnaireForm = () => {
  const { generateCaseStudy } = useApiCalls();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  // Helper function to convert text content to base64 - same as file upload approach
  const textToBase64 = (text) => {
    return btoa(unescape(encodeURIComponent(text)));
  };
  
  // Form state
  const [formData, setFormData] = useState({
    // Client Information
    companyName: '',
    industry: '',
    companySize: '',
    sharingPermission: '',
    companyBackground: '',
    
    // Project Overview
    engagementType: '',
    timeline: '',
    reasonForChoosing: '',
    
    // Key Challenges
    challenge1: '',
    challenge2: '',
    challenge3: '',
    
    // Strategic Objectives
    objective1: '',
    objective2: '',
    objective3: '',
    
    // Quantified Results
    metric1Title: '',
    metric1Details: '',
    metric2Title: '',
    metric2Details: '',
    metric3Title: '',
    metric3Details: '',
    
    // Major Outcomes
    outcome1Title: '',
    outcome1Description: '',
    outcome2Title: '',
    outcome2Description: '',
    outcome3Title: '',
    outcome3Description: '',
    
    // Client Testimonial
    hasTestimonial: true,
    testimonialQuote: '',
    testimonialAuthorName: '',
    testimonialAuthorTitle: '',
    testimonialAuthorCompany: '',
    
    // RISCPoint Contributions
    contribution1Area: '',
    contribution1Details: '',
    contribution2Area: '',
    contribution2Details: '',
    contribution3Area: '',
    contribution3Details: '',
    contribution4Area: '',
    contribution4Details: '',
    
  });

  const [expandedSections, setExpandedSections] = useState({
    clientInfo: true,
    projectOverview: false,
    challenges: false,
    objectives: false,
    results: false,
    outcomes: false,
    testimonial: false,
    contributions: false,
  });

  // Auto-save to localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('questionnaireFormData');
    if (savedData) {
      try {
        setFormData(JSON.parse(savedData));
      } catch (error) {
        console.error('Error loading saved form data:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('questionnaireFormData', JSON.stringify(formData));
  }, [formData]);

  // Note: We don't need job polling here since we redirect to main page
  // The main CaseStudyGenerator will handle all polling and completion logic

  // Define required fields for better UI organization
  // Dynamic required fields based on testimonial availability
  const getRequiredFields = () => {
    const baseFields = [
      'companyName', 'industry', 'engagementType', 'challenge1', 'challenge2',
      'objective1', 'objective2', 'metric1Title', 'metric1Details',
      'metric2Title', 'metric2Details', 'contribution1Area', 'contribution1Details',
      'contribution2Area', 'contribution2Details'
    ];
    
    if (formData.hasTestimonial) {
      baseFields.push('testimonialQuote', 'testimonialAuthorName');
    }
    
    return baseFields;
  };
  
  const requiredFields = getRequiredFields();

  // Section requirements definition
  const sectionRequirements = {
    clientInfo: {
      name: 'Client Information',
      required: true,
      requiredFields: ['companyName', 'industry'],
      optionalFields: ['companySize', 'sharingPermission', 'companyBackground']
    },
    projectOverview: {
      name: 'Project Overview', 
      required: true,
      requiredFields: ['engagementType'],
      optionalFields: ['timeline', 'reasonForChoosing']
    },
    challenges: {
      name: 'Key Challenges',
      required: true, 
      requiredFields: ['challenge1', 'challenge2'],
      optionalFields: ['challenge3']
    },
    objectives: {
      name: 'Strategic Objectives',
      required: true,
      requiredFields: ['objective1', 'objective2'], 
      optionalFields: ['objective3']
    },
    results: {
      name: 'Quantified Results',
      required: true,
      requiredFields: ['metric1Title', 'metric1Details', 'metric2Title', 'metric2Details'],
      optionalFields: ['metric3Title', 'metric3Details'],
      critical: true
    },
    outcomes: {
      name: 'Major Outcomes',
      required: false,
      requiredFields: [],
      optionalFields: ['outcome1Title', 'outcome1Description', 'outcome2Title', 'outcome2Description', 'outcome3Title', 'outcome3Description']
    },
    testimonial: {
      name: 'Client Testimonial', 
      required: formData.hasTestimonial,
      requiredFields: formData.hasTestimonial ? ['testimonialQuote', 'testimonialAuthorName'] : [],
      optionalFields: formData.hasTestimonial ? ['testimonialAuthorTitle', 'testimonialAuthorCompany'] : [],
      critical: formData.hasTestimonial
    },
    contributions: {
      name: 'RISCPoint Contributions',
      required: true,
      requiredFields: ['contribution1Area', 'contribution1Details', 'contribution2Area', 'contribution2Details'],
      optionalFields: ['contribution3Area', 'contribution3Details', 'contribution4Area', 'contribution4Details']
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper functions for requirements tracking
  const getRequiredFieldsCompleted = () => {
    return requiredFields.filter(field => formData[field]?.trim()).length;
  };

  const getTotalOptionalFields = () => {
    return Object.values(sectionRequirements).reduce((total, section) => 
      total + section.optionalFields.length, 0
    );
  };

  const getOptionalFieldsCompleted = () => {
    const allOptionalFields = Object.values(sectionRequirements).flatMap(section => 
      section.optionalFields
    );
    return allOptionalFields.filter(field => formData[field]?.trim()).length;
  };

  const getSectionCompletion = (sectionKey) => {
    const section = sectionRequirements[sectionKey];
    if (!section) return { completed: 0, total: 0, isComplete: false };
    
    const requiredCompleted = section.requiredFields.filter(field => formData[field]?.trim()).length;
    const totalRequired = section.requiredFields.length;
    const optionalCompleted = section.optionalFields.filter(field => formData[field]?.trim()).length;
    const totalOptional = section.optionalFields.length;
    
    return {
      requiredCompleted,
      totalRequired,
      optionalCompleted,
      totalOptional,
      isRequiredComplete: requiredCompleted === totalRequired,
      hasOptionalContent: optionalCompleted > 0
    };
  };

  const getSectionStatus = (sectionKey) => {
    const section = sectionRequirements[sectionKey];
    const completion = getSectionCompletion(sectionKey);
    
    if (section.required && !completion.isRequiredComplete) {
      return 'incomplete'; // Required section with missing required fields
    } else if (section.required && completion.isRequiredComplete) {
      return 'complete'; // Required section fully complete
    } else {
      return 'optional'; // Optional section
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const convertFormToMarkdown = () => {
    const formattedDate = new Date().toLocaleDateString();
    
    return `# RISCPoint Case Study Questionnaire

**Estimated Completion Time:** 15-20 minutes  
**Purpose:** This questionnaire provides the essential information our AI agents need to create a compelling case study.

---

## 1. Client Information

**Company Name:** ${formData.companyName}  
**Industry:** ${formData.industry}  
**Company Size/Type:** ${formData.companySize}

**Sharing Permission:** ${formData.sharingPermission}

**Company Background:**  
${formData.companyBackground}

---

## 2. Project Overview

**Engagement Type/Project Name:** ${formData.engagementType}

**Timeline:** ${formData.timeline}

**Primary Reason for Choosing RISCPoint:**  
${formData.reasonForChoosing}

---

## 3. Key Challenges

**Challenge 1:**
${formData.challenge1}

**Challenge 2:**
${formData.challenge2}

${formData.challenge3 ? `**Challenge 3:**
${formData.challenge3}` : ''}

---

## 4. Strategic Objectives

**Objective 1:**
${formData.objective1}

**Objective 2:**
${formData.objective2}

${formData.objective3 ? `**Objective 3:**
${formData.objective3}` : ''}

---

## 5. Quantified Results ⭐ CRITICAL SECTION

**Metric 1:**
- **Title:** ${formData.metric1Title}
- **Details/Numbers:** ${formData.metric1Details}

**Metric 2:**
- **Title:** ${formData.metric2Title}
- **Details/Numbers:** ${formData.metric2Details}

${formData.metric3Title && formData.metric3Details ? `**Metric 3:**
- **Title:** ${formData.metric3Title}
- **Details/Numbers:** ${formData.metric3Details}` : ''}

---

## 6. Major Outcomes/Accomplishments

**Outcome 1:**
- **Title:** ${formData.outcome1Title}
- **Brief Description:** ${formData.outcome1Description}

**Outcome 2:**
- **Title:** ${formData.outcome2Title}
- **Brief Description:** ${formData.outcome2Description}

${formData.outcome3Title && formData.outcome3Description ? `**Outcome 3:**
- **Title:** ${formData.outcome3Title}
- **Brief Description:** ${formData.outcome3Description}` : ''}

---

## 7. Client Testimonial ${formData.hasTestimonial ? '⭐ CRITICAL SECTION' : '(Not Available)'}

${formData.hasTestimonial ? `**Has Testimonial:** Yes

**Quote:**
"${formData.testimonialQuote}"

**Author Information:**
- **Name:** ${formData.testimonialAuthorName}
- **Title:** ${formData.testimonialAuthorTitle}
- **Company:** ${formData.testimonialAuthorCompany}` : `**Has Testimonial:** No

**Note:** Client testimonial not available for this case study.`}

---

## 8. RISCPoint's Specific Contributions

**Contribution 1:**
- **Area:** ${formData.contribution1Area}
- **Details:** ${formData.contribution1Details}

**Contribution 2:**
- **Area:** ${formData.contribution2Area}
- **Details:** ${formData.contribution2Details}

${formData.contribution3Area && formData.contribution3Details ? `**Contribution 3:**
- **Area:** ${formData.contribution3Area}
- **Details:** ${formData.contribution3Details}` : ''}

${formData.contribution4Area && formData.contribution4Details ? `**Contribution 4:**
- **Area:** ${formData.contribution4Area}
- **Details:** ${formData.contribution4Details}` : ''}

---

**Generated:** ${formattedDate}

---

*This questionnaire provides the essential information for creating a compelling case study that showcases client success and RISCPoint's expertise.*`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionError(null);

    // Basic validation using the defined required fields
    const missingFields = requiredFields.filter(field => !formData[field]?.trim());
    
    if (missingFields.length > 0) {
      setSubmissionError(`Please fill in the required fields: ${missingFields.join(', ')}`);
      setIsSubmitting(false);
      return;
    }

    try {
      logger.log("Starting questionnaire generation process...");

      // Convert markdown content to base64 - same as file upload
      const markdownContent = convertFormToMarkdown();
      const fileContent = textToBase64(markdownContent);

      // Send to generation endpoint with same format as file upload
      const generationResult = await generateCaseStudy({
        file_content: fileContent,
        filename: 'questionnaire_form.md',
        ...DEFAULT_GENERATION_OPTIONS, // Include the same options as file upload
      });

      // Use job_id (not jobId) to match backend response - same as file upload
      const jobId = generationResult.job_id;

      // Save job to session for persistence across refreshes - same as file upload
      jobSession.save(jobId, "generation");
      logger.log("Questionnaire generation started, job ID:", jobId);

      // Clear form data since we're redirecting (user won't return to this form)
      localStorage.removeItem('questionnaireFormData');
      
      // Redirect to main page and include job_id so CaseStudyGenerator can auto-open processing modal
      logger.log("Redirecting to main page for processing...");
      window.location.href = `/?job_id=${encodeURIComponent(jobId)}`;
      
    } catch (error) {
      logger.error("Questionnaire generation failed:", error);
      setSubmissionError(error.message || 'Failed to submit questionnaire. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  const getCompletedRequiredSections = () => {
    return Object.entries(sectionRequirements).filter(([key, section]) => {
      if (!section.required) return false;
      const completion = getSectionCompletion(key);
      return completion.isRequiredComplete;
    }).length;
  };

  const getTotalRequiredSections = () => {
    return Object.values(sectionRequirements).filter(section => section.required).length;
  };

  // Note: All completion handling is now done by the main CaseStudyGenerator
  // This component only handles form submission and redirects to main page

  return (
    <div className="min-h-screen bg-white bg-opacity-90 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Case Study Questionnaire</h1>
              <p className="text-gray-600 mt-1">
                Fill out this guided form to create your case study. Auto-saves as you go.
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Progress</div>
              <div className="text-lg font-semibold text-blue-600">
                {getCompletedRequiredSections()}/{getTotalRequiredSections()} required sections
              </div>
            </div>
          </div>
        </div>

        {/* Requirements Overview Panel */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6 border border-blue-200">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 font-semibold">ℹ️</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Form Requirements Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white rounded-lg p-4 border border-red-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-red-800">Required Fields</h4>
                    <span className="text-sm px-2 py-1 bg-red-100 text-red-800 rounded-full font-medium">
                      Must Complete
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-red-100 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${requiredFields.length > 0 ? (getRequiredFieldsCompleted() / requiredFields.length) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-red-800">
                      {getRequiredFieldsCompleted()}/{requiredFields.length}
                    </span>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-green-800">Optional Fields</h4>
                    <span className="text-sm px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                      Enhance Quality
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-green-100 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${getTotalOptionalFields() > 0 ? (getOptionalFieldsCompleted() / getTotalOptionalFields()) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-green-800">
                      {getOptionalFieldsCompleted()}/{getTotalOptionalFields()}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                <span className="font-medium text-red-600">Required fields</span> are essential for generating a complete case study. 
                <span className="font-medium text-green-600 ml-2">Optional fields</span> enhance the quality and depth of your case study.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Section 1: Client Information */}
          <div className={`bg-white rounded-lg shadow-sm mb-4 ${getSectionStatus('clientInfo') === 'incomplete' ? 'border-l-4 border-red-500' : getSectionStatus('clientInfo') === 'complete' ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}>
            <button
              type="button"
              onClick={() => toggleSection('clientInfo')}
              className="w-full text-left p-6 hover:bg-gray-50 rounded-t-lg border-b border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">1. Client Information</h2>
                    {sectionRequirements.clientInfo.required && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSectionStatus('clientInfo') === 'complete' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {getSectionStatus('clientInfo') === 'complete' ? '✓ REQUIRED - Complete' : '⚠️ REQUIRED'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Company details and background</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {(() => {
                      const completion = getSectionCompletion('clientInfo');
                      return `${completion.requiredCompleted}/${completion.totalRequired} required • ${completion.optionalCompleted}/${completion.totalOptional} optional`;
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">
                    {expandedSections.clientInfo ? '▼' : '▶'}
                  </span>
                </div>
              </div>
            </button>
            
            {expandedSections.clientInfo && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Company Name</span>
                      <span className="text-red-500 font-bold text-base">*</span>
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !formData.companyName?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                    }`}
                    placeholder="Enter company name"
                  />
                  {!formData.companyName?.trim() && (
                    <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="flex items-center space-x-2">
                        <span>Industry</span>
                        <span className="text-red-500 font-bold text-base">*</span>
                        <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                      </span>
                    </label>
                    <div className="space-y-2">
                      <select
                        value={['Healthcare', 'Financial Services', 'Technology/SaaS', 'Manufacturing', 'Government/Public Sector', 'Defense Contractor', 'Education', 'Professional Services', 'Energy/Utilities', 'Retail/E-commerce', 'Non-Profit', 'Real Estate'].includes(formData.industry) ? formData.industry : 'Custom'}
                        onChange={(e) => {
                          if (e.target.value === 'Custom') {
                            handleInputChange('industry', '');
                          } else {
                            handleInputChange('industry', e.target.value);
                          }
                        }}
                        className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !formData.industry?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                        }`}
                      >
                        <option value="">Select industry...</option>
                        <optgroup label="Healthcare & Life Sciences">
                          <option value="Healthcare">Healthcare</option>
                        </optgroup>
                        <optgroup label="Financial Services">
                          <option value="Financial Services">Financial Services</option>
                        </optgroup>
                        <optgroup label="Technology">
                          <option value="Technology/SaaS">Technology/SaaS</option>
                        </optgroup>
                        <optgroup label="Manufacturing">
                          <option value="Manufacturing">Manufacturing</option>
                        </optgroup>
                        <optgroup label="Public Sector">
                          <option value="Government/Public Sector">Government/Public Sector</option>
                          <option value="Defense Contractor">Defense Contractor</option>
                          <option value="Education">Education</option>
                        </optgroup>
                        <optgroup label="Other Industries">
                          <option value="Professional Services">Professional Services</option>
                          <option value="Energy/Utilities">Energy/Utilities</option>
                          <option value="Retail/E-commerce">Retail/E-commerce</option>
                          <option value="Non-Profit">Non-Profit</option>
                          <option value="Real Estate">Real Estate</option>
                        </optgroup>
                        <option value="Custom">Custom (enter your own)</option>
                      </select>
                      
                      {(!['Healthcare', 'Financial Services', 'Technology/SaaS', 'Manufacturing', 'Government/Public Sector', 'Defense Contractor', 'Education', 'Professional Services', 'Energy/Utilities', 'Retail/E-commerce', 'Non-Profit', 'Real Estate'].includes(formData.industry) || formData.industry === '') && (
                        <input
                          type="text"
                          value={['Healthcare', 'Financial Services', 'Technology/SaaS', 'Manufacturing', 'Government/Public Sector', 'Defense Contractor', 'Education', 'Professional Services', 'Energy/Utilities', 'Retail/E-commerce', 'Non-Profit', 'Real Estate'].includes(formData.industry) ? '' : formData.industry}
                          onChange={(e) => handleInputChange('industry', e.target.value)}
                          className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            !formData.industry?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                          }`}
                          placeholder="Enter custom industry (e.g., Healthcare SaaS, Aerospace)"
                        />
                      )}
                    </div>
                    {!formData.industry?.trim() && (
                      <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="flex items-center space-x-2">
                        <span>Company Size</span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                      </span>
                    </label>
                    <select
                      value={formData.companySize}
                      onChange={(e) => handleInputChange('companySize', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select company size...</option>
                      <option value="Small">Small (1-50 employees)</option>
                      <option value="Medium">Medium (51-500 employees)</option>
                      <option value="Large">Large (500+ employees)</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <span className="flex items-center space-x-2">
                      <span>Sharing Permission</span>
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                    </span>
                  </label>
                  <div className="space-y-2">
                    {['Public case study approved', 'Anonymized case study only', 'Internal use only'].map(option => (
                      <label key={option} className="flex items-center">
                        <input
                          type="radio"
                          name="sharingPermission"
                          value={option}
                          checked={formData.sharingPermission === option}
                          onChange={(e) => handleInputChange('sharingPermission', e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Company Background (2-3 sentences)</span>
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                    </span>
                  </label>
                  <textarea
                    value={formData.companyBackground}
                    onChange={(e) => handleInputChange('companyBackground', e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Brief description of what your company does, market position, and key offerings"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Project Overview */}
          <div className={`bg-white rounded-lg shadow-sm mb-4 ${getSectionStatus('projectOverview') === 'incomplete' ? 'border-l-4 border-red-500' : getSectionStatus('projectOverview') === 'complete' ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}>
            <button
              type="button"
              onClick={() => toggleSection('projectOverview')}
              className="w-full text-left p-6 hover:bg-gray-50 rounded-t-lg border-b border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">2. Project Overview</h2>
                    {sectionRequirements.projectOverview.required && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSectionStatus('projectOverview') === 'complete' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {getSectionStatus('projectOverview') === 'complete' ? '✓ REQUIRED - Complete' : '⚠️ REQUIRED'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Engagement details and timeline</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {(() => {
                      const completion = getSectionCompletion('projectOverview');
                      return `${completion.requiredCompleted}/${completion.totalRequired} required • ${completion.optionalCompleted}/${completion.totalOptional} optional`;
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">
                    {expandedSections.projectOverview ? '▼' : '▶'}
                  </span>
                </div>
              </div>
            </button>
            
            {expandedSections.projectOverview && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Engagement Type/Project Name</span>
                      <span className="text-red-500 font-bold text-base">*</span>
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.engagementType}
                    onChange={(e) => handleInputChange('engagementType', e.target.value)}
                    className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !formData.engagementType?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                    }`}
                    placeholder="e.g., FedRAMP Authorization, SOC 2 Compliance, Cloud Migration"
                  />
                  {!formData.engagementType?.trim() && (
                    <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Timeline</span>
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.timeline}
                    onChange={(e) => handleInputChange('timeline', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 12-month engagement (Jan 2023 - Dec 2023)"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Primary Reason for Choosing RISCPoint</span>
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                    </span>
                  </label>
                  <textarea
                    value={formData.reasonForChoosing}
                    onChange={(e) => handleInputChange('reasonForChoosing', e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="If the client provided a reason for choosing RISCPoint over other firms, include it here"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Key Challenges */}
          <div className={`bg-white rounded-lg shadow-sm mb-4 ${getSectionStatus('challenges') === 'incomplete' ? 'border-l-4 border-red-500' : getSectionStatus('challenges') === 'complete' ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}>
            <button
              type="button"
              onClick={() => toggleSection('challenges')}
              className="w-full text-left p-6 hover:bg-gray-50 rounded-t-lg border-b border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">3. Key Challenges</h2>
                    {sectionRequirements.challenges.required && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSectionStatus('challenges') === 'complete' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {getSectionStatus('challenges') === 'complete' ? '✓ REQUIRED - Complete' : '⚠️ REQUIRED'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Challenges the client was trying to overcome</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {(() => {
                      const completion = getSectionCompletion('challenges');
                      return `${completion.requiredCompleted}/${completion.totalRequired} required • ${completion.optionalCompleted}/${completion.totalOptional} optional`;
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">
                    {expandedSections.challenges ? '▼' : '▶'}
                  </span>
                </div>
              </div>
            </button>
            
            {expandedSections.challenges && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Challenge 1</span>
                      <span className="text-red-500 font-bold text-base">*</span>
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                    </span>
                  </label>
                  <textarea
                    value={formData.challenge1}
                    onChange={(e) => handleInputChange('challenge1', e.target.value)}
                    rows="2"
                    className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !formData.challenge1?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                    }`}
                    placeholder="Describe the first major challenge"
                  />
                  {!formData.challenge1?.trim() && (
                    <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Challenge 2</span>
                      <span className="text-red-500 font-bold text-base">*</span>
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                    </span>
                  </label>
                  <textarea
                    value={formData.challenge2}
                    onChange={(e) => handleInputChange('challenge2', e.target.value)}
                    rows="2"
                    className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !formData.challenge2?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                    }`}
                    placeholder="Describe the second major challenge"
                  />
                  {!formData.challenge2?.trim() && (
                    <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Challenge 3</span>
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                    </span>
                  </label>
                  <textarea
                    value={formData.challenge3}
                    onChange={(e) => handleInputChange('challenge3', e.target.value)}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe a third challenge if applicable"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Strategic Objectives */}
          <div className={`bg-white rounded-lg shadow-sm mb-4 ${getSectionStatus('objectives') === 'incomplete' ? 'border-l-4 border-red-500' : getSectionStatus('objectives') === 'complete' ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}>
            <button
              type="button"
              onClick={() => toggleSection('objectives')}
              className="w-full text-left p-6 hover:bg-gray-50 rounded-t-lg border-b border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">4. Strategic Objectives</h2>
                    {sectionRequirements.objectives.required && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSectionStatus('objectives') === 'complete' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {getSectionStatus('objectives') === 'complete' ? '✓ REQUIRED - Complete' : '⚠️ REQUIRED'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">How RISCPoint planned to tackle the engagement</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {(() => {
                      const completion = getSectionCompletion('objectives');
                      return `${completion.requiredCompleted}/${completion.totalRequired} required • ${completion.optionalCompleted}/${completion.totalOptional} optional`;
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">
                    {expandedSections.objectives ? '▼' : '▶'}
                  </span>
                </div>
              </div>
            </button>
            
            {expandedSections.objectives && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Objective 1</span>
                      <span className="text-red-500 font-bold text-base">*</span>
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.objective1}
                    onChange={(e) => handleInputChange('objective1', e.target.value)}
                    className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !formData.objective1?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                    }`}
                    placeholder="Primary objective RISCPoint aimed to achieve"
                  />
                  {!formData.objective1?.trim() && (
                    <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Objective 2</span>
                      <span className="text-red-500 font-bold text-base">*</span>
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.objective2}
                    onChange={(e) => handleInputChange('objective2', e.target.value)}
                    className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !formData.objective2?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                    }`}
                    placeholder="Second key objective"
                  />
                  {!formData.objective2?.trim() && (
                    <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Objective 3</span>
                      <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.objective3}
                    onChange={(e) => handleInputChange('objective3', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Third objective if applicable"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 5: Quantified Results - CRITICAL */}
          <div className={`bg-white rounded-lg shadow-sm mb-4 ${getSectionStatus('results') === 'incomplete' ? 'border-l-4 border-red-500' : getSectionStatus('results') === 'complete' ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}>
            <button
              type="button"
              onClick={() => toggleSection('results')}
              className="w-full text-left p-6 hover:bg-gray-50 rounded-t-lg border-b border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">5. Quantified Results</h2>
                    {sectionRequirements.results.required && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSectionStatus('results') === 'complete' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {getSectionStatus('results') === 'complete' ? '✓ REQUIRED - Complete' : '⚠️ REQUIRED'}
                      </span>
                    )}
                    {sectionRequirements.results.critical && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                        ⭐ CRITICAL
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Engagement highlights: cost savings, time savings, and key achievements</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {(() => {
                      const completion = getSectionCompletion('results');
                      return `${completion.requiredCompleted}/${completion.totalRequired} required • ${completion.optionalCompleted}/${completion.totalOptional} optional`;
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">
                    {expandedSections.results ? '▼' : '▶'}
                  </span>
                </div>
              </div>
            </button>
            
            {expandedSections.results && (
              <div className="p-6 space-y-6">
                <div className="bg-yellow-50 p-4 rounded-md">
                  <p className="text-sm text-yellow-800">
                    💡 These metrics become the prominent "Key Metrics" section. Be specific with numbers and percentages.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center space-x-2">
                    <span>Metric 1</span>
                    <span className="text-red-500 font-bold text-base">*</span>
                    <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <div className="space-y-2">
                        <select
                          value={['Cost Savings', 'Time Savings', 'Revenue Growth', 'Efficiency Improvement', 'Risk Reduction', 'Compliance Achievement', 'Performance Boost', 'Security Enhancement'].includes(formData.metric1Title) ? formData.metric1Title : 'Custom'}
                          onChange={(e) => {
                            if (e.target.value === 'Custom') {
                              handleInputChange('metric1Title', '');
                            } else {
                              handleInputChange('metric1Title', e.target.value);
                            }
                          }}
                          className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            !formData.metric1Title?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                          }`}
                        >
                          <option value="">Select a metric type...</option>
                          <option value="Cost Savings">Cost Savings</option>
                          <option value="Time Savings">Time Savings</option>
                          <option value="Revenue Growth">Revenue Growth</option>
                          <option value="Efficiency Improvement">Efficiency Improvement</option>
                          <option value="Risk Reduction">Risk Reduction</option>
                          <option value="Compliance Achievement">Compliance Achievement</option>
                          <option value="Performance Boost">Performance Boost</option>
                          <option value="Security Enhancement">Security Enhancement</option>
                          <option value="Custom">Custom (enter your own)</option>
                        </select>
                        
                        {(!['Cost Savings', 'Time Savings', 'Revenue Growth', 'Efficiency Improvement', 'Risk Reduction', 'Compliance Achievement', 'Performance Boost', 'Security Enhancement'].includes(formData.metric1Title) || formData.metric1Title === '') && (
                          <input
                            type="text"
                            value={['Cost Savings', 'Time Savings', 'Revenue Growth', 'Efficiency Improvement', 'Risk Reduction', 'Compliance Achievement', 'Performance Boost', 'Security Enhancement'].includes(formData.metric1Title) ? '' : formData.metric1Title}
                            onChange={(e) => handleInputChange('metric1Title', e.target.value)}
                            className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              !formData.metric1Title?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                            }`}
                            placeholder="Enter custom metric title (e.g., 40% Cost Reduction)"
                          />
                        )}
                      </div>
                      {!formData.metric1Title?.trim() && (
                        <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Details/Numbers</label>
                      <input
                        type="text"
                        value={formData.metric1Details}
                        onChange={(e) => handleInputChange('metric1Details', e.target.value)}
                        className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !formData.metric1Details?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                        }`}
                        placeholder="e.g., Reduced compliance costs from $500K to $300K"
                      />
                      {!formData.metric1Details?.trim() && (
                        <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center space-x-2">
                    <span>Metric 2</span>
                    <span className="text-red-500 font-bold text-base">*</span>
                    <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <div className="space-y-2">
                        <select
                          value={['Cost Savings', 'Time Savings', 'Revenue Growth', 'Efficiency Improvement', 'Risk Reduction', 'Compliance Achievement', 'Performance Boost', 'Security Enhancement'].includes(formData.metric2Title) ? formData.metric2Title : 'Custom'}
                          onChange={(e) => {
                            if (e.target.value === 'Custom') {
                              handleInputChange('metric2Title', '');
                            } else {
                              handleInputChange('metric2Title', e.target.value);
                            }
                          }}
                          className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            !formData.metric2Title?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                          }`}
                        >
                          <option value="">Select a metric type...</option>
                          <option value="Cost Savings">Cost Savings</option>
                          <option value="Time Savings">Time Savings</option>
                          <option value="Revenue Growth">Revenue Growth</option>
                          <option value="Efficiency Improvement">Efficiency Improvement</option>
                          <option value="Risk Reduction">Risk Reduction</option>
                          <option value="Compliance Achievement">Compliance Achievement</option>
                          <option value="Performance Boost">Performance Boost</option>
                          <option value="Security Enhancement">Security Enhancement</option>
                          <option value="Custom">Custom (enter your own)</option>
                        </select>
                        
                        {(!['Cost Savings', 'Time Savings', 'Revenue Growth', 'Efficiency Improvement', 'Risk Reduction', 'Compliance Achievement', 'Performance Boost', 'Security Enhancement'].includes(formData.metric2Title) || formData.metric2Title === '') && (
                          <input
                            type="text"
                            value={['Cost Savings', 'Time Savings', 'Revenue Growth', 'Efficiency Improvement', 'Risk Reduction', 'Compliance Achievement', 'Performance Boost', 'Security Enhancement'].includes(formData.metric2Title) ? '' : formData.metric2Title}
                            onChange={(e) => handleInputChange('metric2Title', e.target.value)}
                            className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              !formData.metric2Title?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                            }`}
                            placeholder="Enter custom metric title (e.g., 6 Month Timeline)"
                          />
                        )}
                      </div>
                      {!formData.metric2Title?.trim() && (
                        <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Details/Numbers</label>
                      <input
                        type="text"
                        value={formData.metric2Details}
                        onChange={(e) => handleInputChange('metric2Details', e.target.value)}
                        className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !formData.metric2Details?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                        }`}
                        placeholder="e.g., Completed FedRAMP authorization 2 months ahead of schedule"
                      />
                      {!formData.metric2Details?.trim() && (
                        <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center space-x-2">
                    <span>Metric 3</span>
                    <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <div className="space-y-2">
                        <select
                          value={['Cost Savings', 'Time Savings', 'Revenue Growth', 'Efficiency Improvement', 'Risk Reduction', 'Compliance Achievement', 'Performance Boost', 'Security Enhancement'].includes(formData.metric3Title) ? formData.metric3Title : 'Custom'}
                          onChange={(e) => {
                            if (e.target.value === 'Custom') {
                              handleInputChange('metric3Title', '');
                            } else {
                              handleInputChange('metric3Title', e.target.value);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select a metric type...</option>
                          <option value="Cost Savings">Cost Savings</option>
                          <option value="Time Savings">Time Savings</option>
                          <option value="Revenue Growth">Revenue Growth</option>
                          <option value="Efficiency Improvement">Efficiency Improvement</option>
                          <option value="Risk Reduction">Risk Reduction</option>
                          <option value="Compliance Achievement">Compliance Achievement</option>
                          <option value="Performance Boost">Performance Boost</option>
                          <option value="Security Enhancement">Security Enhancement</option>
                          <option value="Custom">Custom (enter your own)</option>
                        </select>
                        
                        {(!['Cost Savings', 'Time Savings', 'Revenue Growth', 'Efficiency Improvement', 'Risk Reduction', 'Compliance Achievement', 'Performance Boost', 'Security Enhancement'].includes(formData.metric3Title) || formData.metric3Title === '') && (
                          <input
                            type="text"
                            value={['Cost Savings', 'Time Savings', 'Revenue Growth', 'Efficiency Improvement', 'Risk Reduction', 'Compliance Achievement', 'Performance Boost', 'Security Enhancement'].includes(formData.metric3Title) ? '' : formData.metric3Title}
                            onChange={(e) => handleInputChange('metric3Title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter custom metric title (e.g., Zero Critical Findings)"
                          />
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Details/Numbers</label>
                      <input
                        type="text"
                        value={formData.metric3Details}
                        onChange={(e) => handleInputChange('metric3Details', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Passed all security controls on first assessment"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 6: Major Outcomes */}
          <div className={`bg-white rounded-lg shadow-sm mb-4 ${getSectionStatus('outcomes') === 'incomplete' ? 'border-l-4 border-gray-300' : getSectionStatus('outcomes') === 'complete' ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}>
            <button
              type="button"
              onClick={() => toggleSection('outcomes')}
              className="w-full text-left p-6 hover:bg-gray-50 rounded-t-lg border-b border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">6. Major Outcomes</h2>
                    {!sectionRequirements.outcomes.required && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        OPTIONAL
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Positive business outcomes and benefits from successful engagement</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {(() => {
                      const completion = getSectionCompletion('outcomes');
                      return `${completion.requiredCompleted}/${completion.totalRequired} required • ${completion.optionalCompleted}/${completion.totalOptional} optional`;
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">
                    {expandedSections.outcomes ? '▼' : '▶'}
                  </span>
                </div>
              </div>
            </button>
            
            {expandedSections.outcomes && (
              <div className="p-6 space-y-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Outcome 1</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={formData.outcome1Title}
                      onChange={(e) => handleInputChange('outcome1Title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Federal Market Access"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.outcome1Description}
                      onChange={(e) => handleInputChange('outcome1Description', e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Brief description of this major outcome"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Outcome 2</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={formData.outcome2Title}
                      onChange={(e) => handleInputChange('outcome2Title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Enhanced Security Posture"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.outcome2Description}
                      onChange={(e) => handleInputChange('outcome2Description', e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Brief description of this major outcome"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Outcome 3 (Optional)</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={formData.outcome3Title}
                      onChange={(e) => handleInputChange('outcome3Title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Operational Efficiency"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.outcome3Description}
                      onChange={(e) => handleInputChange('outcome3Description', e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Brief description of this outcome if applicable"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 7: Client Testimonial - CRITICAL */}
          <div className={`bg-white rounded-lg shadow-sm mb-4 ${getSectionStatus('testimonial') === 'incomplete' ? 'border-l-4 border-red-500' : getSectionStatus('testimonial') === 'complete' ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}>
            <button
              type="button"
              onClick={() => toggleSection('testimonial')}
              className="w-full text-left p-6 hover:bg-gray-50 rounded-t-lg border-b border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">7. Client Testimonial</h2>
                    {sectionRequirements.testimonial.required && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSectionStatus('testimonial') === 'complete' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {getSectionStatus('testimonial') === 'complete' ? '✓ REQUIRED - Complete' : '⚠️ REQUIRED'}
                      </span>
                    )}
                    {sectionRequirements.testimonial.critical && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                        ⭐ CRITICAL
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Powerful quote about working with RISCPoint</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {(() => {
                      const completion = getSectionCompletion('testimonial');
                      return `${completion.requiredCompleted}/${completion.totalRequired} required • ${completion.optionalCompleted}/${completion.totalOptional} optional`;
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">
                    {expandedSections.testimonial ? '▼' : '▶'}
                  </span>
                </div>
              </div>
            </button>
            
            {expandedSections.testimonial && (
              <div className="p-6 space-y-4">
                {/* Testimonial availability checkbox */}
                <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="noTestimonialAvailable"
                      checked={!formData.hasTestimonial}
                      onChange={(e) => handleInputChange('hasTestimonial', !e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="noTestimonialAvailable" className="text-sm font-medium text-blue-900">
                      No testimonial available for this case study
                    </label>
                  </div>
                  <p className="text-xs text-blue-700 mt-2 ml-7">
                    Check this box if the client has not provided a testimonial or prefers not to include one.
                  </p>
                </div>

                {formData.hasTestimonial && (
                  <>
                    <div className="bg-yellow-50 p-4 rounded-md">
                      <p className="text-sm text-yellow-800">
                        💡 This quote will be prominently featured in the case study. Make it specific and impactful.
                      </p>
                    </div>
                    
                    <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <span>Quote</span>
                      <span className="text-red-500 font-bold text-base">*</span>
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                    </span>
                  </label>
                  <textarea
                    value={formData.testimonialQuote}
                    onChange={(e) => handleInputChange('testimonialQuote', e.target.value)}
                    rows="3"
                    className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !formData.testimonialQuote?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                    }`}
                    placeholder="One strong, specific quote about working with RISCPoint..."
                  />
                  {!formData.testimonialQuote?.trim() && (
                    <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="flex items-center space-x-2">
                        <span>Author Name</span>
                        <span className="text-red-500 font-bold text-base">*</span>
                        <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.testimonialAuthorName}
                      onChange={(e) => handleInputChange('testimonialAuthorName', e.target.value)}
                      className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        !formData.testimonialAuthorName?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                      }`}
                      placeholder="e.g., John Smith"
                    />
                    {!formData.testimonialAuthorName?.trim() && (
                      <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="flex items-center space-x-2">
                        <span>Title</span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.testimonialAuthorTitle}
                      onChange={(e) => handleInputChange('testimonialAuthorTitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., CTO"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="flex items-center space-x-2">
                        <span>Company</span>
                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.testimonialAuthorCompany}
                      onChange={(e) => handleInputChange('testimonialAuthorCompany', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., TechCorp Inc."
                    />
                  </div>
                </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Section 8: RISCPoint Contributions */}
          <div className={`bg-white rounded-lg shadow-sm mb-4 ${getSectionStatus('contributions') === 'incomplete' ? 'border-l-4 border-red-500' : getSectionStatus('contributions') === 'complete' ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}>
            <button
              type="button"
              onClick={() => toggleSection('contributions')}
              className="w-full text-left p-6 hover:bg-gray-50 rounded-t-lg border-b border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h2 className="text-lg font-semibold text-gray-900">8. RISCPoint Contributions</h2>
                    {sectionRequirements.contributions.required && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSectionStatus('contributions') === 'complete' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {getSectionStatus('contributions') === 'complete' ? '✓ REQUIRED - Complete' : '⚠️ REQUIRED'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Specific steps and methods RISCPoint used to help the client</p>
                  <div className="mt-2 text-xs text-gray-500">
                    {(() => {
                      const completion = getSectionCompletion('contributions');
                      return `${completion.requiredCompleted}/${completion.totalRequired} required • ${completion.optionalCompleted}/${completion.totalOptional} optional`;
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">
                    {expandedSections.contributions ? '▼' : '▶'}
                  </span>
                </div>
              </div>
            </button>
            
            {expandedSections.contributions && (
              <div className="p-6 space-y-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center space-x-2">
                    <span>Contribution 1</span>
                    <span className="text-red-500 font-bold text-base">*</span>
                    <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                      <input
                        type="text"
                        value={formData.contribution1Area}
                        onChange={(e) => handleInputChange('contribution1Area', e.target.value)}
                        className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !formData.contribution1Area?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                        }`}
                        placeholder="e.g., Compliance Strategy"
                      />
                      {!formData.contribution1Area?.trim() && (
                        <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                      <textarea
                        value={formData.contribution1Details}
                        onChange={(e) => handleInputChange('contribution1Details', e.target.value)}
                        rows="2"
                        className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !formData.contribution1Details?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                        }`}
                        placeholder="What specifically did RISCPoint do in this area?"
                      />
                      {!formData.contribution1Details?.trim() && (
                        <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center space-x-2">
                    <span>Contribution 2</span>
                    <span className="text-red-500 font-bold text-base">*</span>
                    <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Required</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                      <input
                        type="text"
                        value={formData.contribution2Area}
                        onChange={(e) => handleInputChange('contribution2Area', e.target.value)}
                        className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !formData.contribution2Area?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                        }`}
                        placeholder="e.g., Technical Implementation"
                      />
                      {!formData.contribution2Area?.trim() && (
                        <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                      <textarea
                        value={formData.contribution2Details}
                        onChange={(e) => handleInputChange('contribution2Details', e.target.value)}
                        rows="2"
                        className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !formData.contribution2Details?.trim() ? 'border-2 border-red-300 bg-red-50' : 'border border-gray-300'
                        }`}
                        placeholder="What specifically did RISCPoint do in this area?"
                      />
                      {!formData.contribution2Details?.trim() && (
                        <p className="mt-1 text-xs text-red-600">This field is required for case study generation</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center space-x-2">
                    <span>Contribution 3</span>
                    <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                      <input
                        type="text"
                        value={formData.contribution3Area}
                        onChange={(e) => handleInputChange('contribution3Area', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Change Management"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                      <textarea
                        value={formData.contribution3Details}
                        onChange={(e) => handleInputChange('contribution3Details', e.target.value)}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="What specifically did RISCPoint do in this area?"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 flex items-center space-x-2">
                    <span>Contribution 4</span>
                    <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-medium">Optional</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                      <input
                        type="text"
                        value={formData.contribution4Area}
                        onChange={(e) => handleInputChange('contribution4Area', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Training & Support"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                      <textarea
                        value={formData.contribution4Details}
                        onChange={(e) => handleInputChange('contribution4Details', e.target.value)}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="What specifically did RISCPoint do in this area?"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>



          {/* Submit Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            {submissionError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{submissionError}</p>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Ready to Generate Case Study</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Ensure all required fields are completed before generating. {getRequiredFieldsCompleted()} of {requiredFields.length} required fields completed.
                </p>
                <div className="mt-2 flex items-center space-x-4 text-xs">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-gray-600">Required ({requiredFields.length} fields)</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-600">Optional ({getTotalOptionalFields()} fields)</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span className="text-gray-600">Critical for quality</span>
                  </div>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isSubmitting ? 'Generating...' : 'Generate Case Study'}
              </button>
            </div>
          </div>
        </form>
      </div>

    </div>
  );
};

export default QuestionnaireForm;